import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormSection } from '@/components/forms/Form';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import {
  useFormations,
  useFormateurs,
  useSalles,
  useSessions,
  useUpsertSession,
} from '@/hooks/queries/useFormation';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { formatDA, formatDateDZ } from '@/lib/format';
import { useRole } from '@/hooks/useRole';
import type {
  SessionFormationRow,
  SessionLieuType,
  SessionStatut,
  SessionType,
} from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  defaultFormationId?: string;
  defaultSousSegmentIds?: string[];
  editing: SessionFormationRow | null;
  onSaved: () => void;
}

export function SessionFormDialog({
  open,
  onOpenChange,
  defaultFormationId,
  defaultSousSegmentIds,
  editing,
  onSaved,
}: Props): JSX.Element {
  const { isAdmin } = useRole();
  const { data: entreprises = [] } = useEntreprises();
  const eurlKostGroup = entreprises.find((e) => e.nom.includes('Kost'));
  const { data: formations = [] } = useFormations({
    sousSegmentIds: defaultSousSegmentIds ?? null,
    statut: 'actif',
  });
  const { data: formateurs = [] } = useFormateurs();
  const { data: salles = [] } = useSalles(eurlKostGroup?.id ?? null);
  const upsert = useUpsertSession();

  const [formationId, setFormationId] = useState('');
  const [type, setType] = useState<SessionType>('inter');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [lieuType, setLieuType] = useState<SessionLieuType>('presentiel');
  const [lieuAdresse, setLieuAdresse] = useState('');
  const [lienVisio, setLienVisio] = useState('');
  const [salleId, setSalleId] = useState('');
  const [formateurId, setFormateurId] = useState('');
  const [coAnimateurs, setCoAnimateurs] = useState<string[]>([]);
  const [capaciteMin, setCapaciteMin] = useState('4');
  const [capaciteMax, setCapaciteMax] = useState('12');
  const [prixSession, setPrixSession] = useState('');
  const [statut, setStatut] = useState<SessionStatut>('planifiee');
  const [error, setError] = useState<string | null>(null);
  const [forceConfirm, setForceConfirm] = useState(false);

  const formationSelected = formations.find((f) => f.id === formationId);

  // Auto-rempli depuis la formation
  useEffect(() => {
    if (!formationSelected) return;
    if (!editing) {
      if (!formateurId && formationSelected.formateur_principal_id) {
        setFormateurId(formationSelected.formateur_principal_id);
      }
      if (!prixSession) {
        const prix =
          type === 'inter'
            ? formationSelected.prix_inter_unitaire
            : type === 'intra'
              ? formationSelected.prix_intra_forfait
              : formationSelected.prix_b2c;
        if (prix) setPrixSession(String(prix));
      }
    }
  }, [formationSelected, type, editing, formateurId, prixSession]);

  // Auto-calcul date_fin selon duree_jours de la formation
  useEffect(() => {
    if (formationSelected?.duree_jours && dateDebut && !editing) {
      const d = new Date(dateDebut);
      d.setDate(d.getDate() + Math.max(0, Math.ceil(Number(formationSelected.duree_jours)) - 1));
      setDateFin(d.toISOString().slice(0, 10));
    }
  }, [formationSelected, dateDebut, editing]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setFormationId(editing.formation_id);
      setType(editing.type);
      setDateDebut(editing.date_debut);
      setDateFin(editing.date_fin);
      setLieuType(editing.lieu_type);
      setLieuAdresse(editing.lieu_adresse ?? '');
      setLienVisio(editing.lien_visio ?? '');
      setSalleId(editing.salle_id ?? '');
      setFormateurId(editing.formateur_principal_id ?? '');
      setCoAnimateurs(editing.formateurs_co_animateurs_ids);
      setCapaciteMin(String(editing.capacite_min));
      setCapaciteMax(String(editing.capacite_max));
      setPrixSession(editing.prix_session ? String(editing.prix_session) : '');
      setStatut(editing.statut);
    } else {
      setFormationId(defaultFormationId ?? '');
      setType('inter');
      const today = new Date();
      today.setDate(today.getDate() + 14);
      setDateDebut(today.toISOString().slice(0, 10));
      setDateFin(today.toISOString().slice(0, 10));
      setLieuType('presentiel');
      setLieuAdresse('');
      setLienVisio('');
      setSalleId('');
      setFormateurId('');
      setCoAnimateurs([]);
      setCapaciteMin('4');
      setCapaciteMax('12');
      setPrixSession('');
      setStatut('ouverte_inscriptions');
    }
    setError(null);
    setForceConfirm(false);
  }, [open, editing, defaultFormationId]);

  // Détection conflit formateur sur la période
  const { data: sessionsFormateur = [] } = useSessions({
    formateurId: formateurId || null,
  });
  const conflits = useMemo(() => {
    if (!formateurId || !dateDebut || !dateFin) return [];
    const start = new Date(dateDebut);
    const end = new Date(dateFin);
    return sessionsFormateur.filter((s) => {
      if (editing && s.id === editing.id) return false;
      if (s.statut === 'annulee') return false;
      const sStart = new Date(s.date_debut);
      const sEnd = new Date(s.date_fin);
      return sStart <= end && sEnd >= start;
    });
  }, [sessionsFormateur, formateurId, dateDebut, dateFin, editing]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formationId) return setError('Formation obligatoire');
    if (!dateDebut || !dateFin) return setError('Dates obligatoires');
    if (dateFin < dateDebut) return setError('Date fin doit être ≥ date début');
    const cMin = Number(capaciteMin);
    const cMax = Number(capaciteMax);
    if (cMax < cMin) return setError('Capacité max doit être ≥ capacité min');
    if (lieuType === 'presentiel' && !salleId && !lieuAdresse.trim())
      return setError('Salle ou adresse obligatoire pour présentiel');
    if (lieuType === 'distanciel' && !lienVisio.trim())
      return setError('Lien visio obligatoire pour distanciel');
    if (conflits.length > 0 && !forceConfirm) {
      setError(
        `Conflit formateur détecté : ${conflits.length} session(s) en chevauchement (${conflits[0]?.reference}). Cochez « Forcer » pour valider quand même (super_admin uniquement).`,
      );
      return;
    }

    try {
      await upsert.mutateAsync({
        id: editing?.id,
        formation_id: formationId,
        type,
        date_debut: dateDebut,
        date_fin: dateFin,
        lieu_type: lieuType,
        lieu_adresse: lieuAdresse || null,
        lien_visio: lienVisio || null,
        salle_id: salleId || null,
        formateur_principal_id: formateurId || null,
        formateurs_co_animateurs_ids: coAnimateurs,
        capacite_min: cMin,
        capacite_max: cMax,
        prix_session: prixSession ? Number(prixSession) : null,
        statut,
      });
      toast.success(editing ? 'Session mise à jour' : 'Session créée');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  const toggleCoAnimateur = (id: string): void => {
    setCoAnimateurs((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `Session ${editing.reference}` : 'Nouvelle session'}
      onSubmit={handleSubmit}
      isSubmitting={upsert.isPending}
      size="xl"
    >
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      {conflits.length > 0 && (
        <div className="space-y-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
          <div className="flex items-start gap-2 font-semibold text-warning">
            <AlertTriangle className="h-3.5 w-3.5" />
            Conflit formateur sur la période
          </div>
          <ul className="ml-5 list-disc text-warning">
            {conflits.slice(0, 3).map((c) => (
              <li key={c.id}>
                {c.reference} — {formatDateDZ(c.date_debut)} → {formatDateDZ(c.date_fin)} (
                {c.statut})
              </li>
            ))}
          </ul>
          {isAdmin && (
            <label className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                checked={forceConfirm}
                onChange={(e) => setForceConfirm(e.target.checked)}
              />
              Forcer la création malgré le conflit (super_admin)
            </label>
          )}
        </div>
      )}

      <FormSection title="Identification">
        <FormSelect
          label="Formation"
          value={formationId}
          onChange={setFormationId}
          required
          options={[
            { value: '', label: '— Sélectionner —' },
            ...formations.map((f) => ({ value: f.id, label: `${f.code} — ${f.titre}` })),
          ]}
          fullWidth
        />
        <FormSelect
          label="Type de session"
          value={type}
          onChange={(v) => setType(v as SessionType)}
          options={[
            { value: 'inter', label: 'Inter-entreprise' },
            { value: 'intra', label: 'Intra-entreprise' },
            { value: 'b2c', label: 'B2C particulier' },
          ]}
        />
        <FormSelect
          label="Statut initial"
          value={statut}
          onChange={(v) => setStatut(v as SessionStatut)}
          options={[
            { value: 'planifiee', label: 'Planifiée' },
            { value: 'ouverte_inscriptions', label: 'Ouverte aux inscriptions' },
          ]}
        />
      </FormSection>

      <FormSection title="Dates">
        <FormField
          label="Date début"
          type="date"
          value={dateDebut}
          onChange={(e) => setDateDebut(e.target.value)}
          required
        />
        <FormField
          label="Date fin"
          type="date"
          value={dateFin}
          onChange={(e) => setDateFin(e.target.value)}
          required
          hint="Auto-calculée depuis la durée formation"
        />
      </FormSection>

      <FormSection title="Lieu">
        <FormSelect
          label="Type de lieu"
          value={lieuType}
          onChange={(v) => setLieuType(v as SessionLieuType)}
          options={[
            { value: 'presentiel', label: 'Présentiel' },
            { value: 'distanciel', label: 'Distanciel' },
            { value: 'blended', label: 'Blended' },
          ]}
        />
        {lieuType !== 'distanciel' && (
          <>
            <FormSelect
              label="Salle (optionnel)"
              value={salleId}
              onChange={setSalleId}
              options={[
                { value: '', label: '— Aucune —' },
                ...salles.map((s) => ({ value: s.id, label: s.nom })),
              ]}
            />
            <FormField
              label="Adresse libre"
              value={lieuAdresse}
              onChange={(e) => setLieuAdresse(e.target.value)}
              fullWidth
              placeholder="Ex: site client, hôtel…"
            />
          </>
        )}
        {lieuType !== 'presentiel' && (
          <FormField
            label="Lien visio (Zoom/Teams)"
            value={lienVisio}
            onChange={(e) => setLienVisio(e.target.value)}
            fullWidth
            placeholder="https://…"
          />
        )}
      </FormSection>

      <FormSection title="Formateur(s)">
        <FormSelect
          label="Formateur principal"
          value={formateurId}
          onChange={setFormateurId}
          fullWidth
          options={[
            { value: '', label: '— Sélectionner —' },
            ...formateurs
              .filter((f) => f.is_active)
              .map((f) => ({
                value: f.id,
                label: `${f.prenom} ${f.nom} (${f.statut})`,
              })),
          ]}
        />
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-xs text-muted-foreground">Co-animateurs (optionnel)</label>
          <div className="flex flex-wrap gap-1">
            {formateurs
              .filter((f) => f.is_active && f.id !== formateurId)
              .map((f) => {
                const active = coAnimateurs.includes(f.id);
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => toggleCoAnimateur(f.id)}
                    className={
                      active
                        ? 'rounded-md border border-primary bg-primary/10 px-2 py-1 text-xs text-primary'
                        : 'rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-secondary'
                    }
                  >
                    {f.prenom} {f.nom[0]}.
                  </button>
                );
              })}
          </div>
        </div>
      </FormSection>

      <FormSection title="Capacité & prix">
        <FormField
          label="Capacité min"
          type="number"
          step="1"
          min="1"
          value={capaciteMin}
          onChange={(e) => setCapaciteMin(e.target.value)}
        />
        <FormField
          label="Capacité max"
          type="number"
          step="1"
          min="1"
          value={capaciteMax}
          onChange={(e) => setCapaciteMax(e.target.value)}
        />
        <FormField
          label="Prix session (DZD)"
          type="number"
          step="0.01"
          min="0"
          value={prixSession}
          onChange={(e) => setPrixSession(e.target.value)}
          hint={
            formationSelected
              ? `Auto depuis formation : ${formatDA(Number(formationSelected.prix_inter_unitaire ?? formationSelected.prix_b2c ?? 0))}`
              : undefined
          }
          fullWidth
        />
      </FormSection>

      {/* placeholder for textarea import */}
      <span className="hidden">
        <FormTextarea label="x" value="" onChange={() => undefined} />
      </span>
    </FormDialog>
  );
}
